// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// modes.ts — three modes, and the test each had to pass is: does a strategy that WINS here LOSE in the
// other two? If two modes reward the same thumb, one of them is a slider and should be cut.
//
//   • nightshift — full information. Every arc is drawn and, at CALM and RESTLESS, so is each minder's
//     NEXT-turn arc, because patrols really are deterministic. It is a geometry problem with a greed
//     clock, and the whole mode is the closing question: bank forty-five and walk, or go back for the
//     twenty-five while the building is still RESTLESS and every step you take is now louder.
//   • brownout — INVERTS WHO HAS VISION. The floor is gloom, so your own sight of the building
//     collapses to five cells, but the minders carry torches: the one place you can see is the one
//     place that can see you. You find bodies by watching a beam sweep through a doorway two rooms
//     away. A tracking-and-inference problem, not a harder geometry one.
//   • hotfloor — the same systems read backwards. Heat STARTS at LOCKDOWN, so you open with the doors
//     locked, the zones searching, the front sealed and the custodian already walking. Detection is
//     not the fail state here; CONTACT is. You are managing a pursuit, not avoiding one.
//
// HOT FLOOR DOES NOT FREEZE HEAT. The design had it pinned at 80, which deleted the slot ledger, the
// decay, the bands, the ratchet and the hysteresis — over half the systems in the game — in the exact
// mode whose own text bills Hold as a primary verb, where it would have been a strict no-op. It now
// simply STARTS at 80 with the ratchet floor already at LOCKDOWN, which is the existing mechanic and
// needs no special case: heat can still climb to 100, and holding is what stops it.
//
// BROWNOUT'S FOG IS RENDER-ONLY. `awareness` never reaches rules.ts; tests/rules.test.ts hashes full
// state with fog on and off and asserts byte equality, so a future "small optimisation" cannot turn a
// presentation choice into a rules branch.

export interface Mode {
  id: string;
  name: string;
  turns: number;
  minders: number;
  lenses: number;
  /** Loot items placed. 0 means the mode has a single objective instead. */
  loot: number;
  /** hotfloor: one dossier in the strongroom, worth this, and it costs 2 slots to carry. */
  objectiveValue: number;
  objectiveSlots: number;
  /** Starting heat. The ratchet floor is derived from it, so hotfloor opens locked down. */
  startHeat: number;
  /** brownout: the whole floor is gloom and breakers are dead. */
  mainsOut: boolean;
  /** brownout: minders carry a torch that forces cells in front of them back to bright. */
  torches: boolean;
  /** brownout: RENDER-ONLY knowledge radius. 0 = full information. */
  awareness: number;
  /** brownout: a silent building carries every din further. */
  dinBonus: number;
  /** hotfloor: a stolen keycard opens any interior door for 1 slot and zero din. */
  keycard: boolean;
  /** hotfloor: only the roof duct is an exit. */
  ductExitOnly: boolean;
  blurb: string;
  /** One line for the reference card, which must state what differs per mode. */
  rule: string;
}

export const MODES: Record<string, Mode> = {
  nightshift: {
    id: 'nightshift',
    name: 'Nightshift',
    turns: 30,
    minders: 3,
    lenses: 2,
    loot: 5,
    objectiveValue: 0,
    objectiveSlots: 0,
    startHeat: 0,
    mainsOut: false,
    torches: false,
    awareness: 0,
    dinBonus: 0,
    keycard: false,
    ductExitOnly: false,
    blurb:
      'One floor, thirty turns, five cases. Everything loud you do leaves a mark on the floor, and a minder who walks onto a mark reads it — so the building is not chasing you, it is chasing what you left behind. Slots you don’t spend are the only thing that cools it down, and every case you carry makes every step you take louder.',
    rule: 'Bank what you can and reach an exit. Unspent slots cool the building.',
  },
  /**
   * TWENTY-FOUR TURNS, AND THE NUMBER IS THE MODE. In gloom a creeping intruder is nearly unseeable,
   * so the design's Brownout had an optimum of "creep on turn one and never stop". Creep costs two
   * slots a cell, so a fully-crept par route consumes the entire clock with no slack at all: creeping
   * everywhere now loses on time rather than on sight, which is the only honest way to price it.
   */
  brownout: {
    id: 'brownout',
    name: 'Brownout',
    turns: 24,
    minders: 4,
    lenses: 0,
    loot: 4,
    objectiveValue: 0,
    objectiveSlots: 0,
    startHeat: 0,
    mainsOut: true,
    torches: true,
    awareness: 5,
    dinBonus: 2,
    keycard: false,
    ductExitOnly: false,
    blurb:
      'You cut the mains on the way in, and it is not easy mode — it takes your eyes too. You see five cells. Everything else you know about this floor you learn from torch beams swinging through doorways, which means the one place with light in it is the one place with a minder in it. The building is silent, so every sound you make travels further than it should.',
    rule: 'The floor is dark and so are you. Watch the torches; they are the only map you get.',
  },
  hotfloor: {
    id: 'hotfloor',
    name: 'Hot Floor',
    // IT STARTS AT SWEEP, NOT AT LOCKDOWN, AND THE SIM IS WHY. Opening at 80 meant the front exit was
    // already sealed, reinforcements were homing in from turn three, and the custodian was deleting the
    // objective while three bodies searched a floor with no gap in it: ninety-eight per cent of runs
    // timed out at zero, under every policy tried, including ones that took no risks and ones that took
    // all of them. A mode no bot can complete is a broken loop, not a hard difficulty.
    // Starting at SWEEP keeps every bit of the mode's identity — doors already locked, the floor split
    // into search zones, arcs already widened, the ratchet already above CALM for good — while leaving
    // the seams between zones open, which is exactly the trap SWEEP was designed around. LOCKDOWN is
    // still one loud mistake away, and now it is something the player earns rather than a starting gun.
    turns: 24,
    minders: 3,
    lenses: 1,
    loot: 0,
    objectiveValue: 60,
    objectiveSlots: 2,
    startHeat: 50,
    mainsOut: false,
    torches: false,
    awareness: 0,
    dinBonus: 0,
    keycard: true,
    ductExitOnly: true,
    blurb:
      'Everything has already gone wrong. The floor is being searched before your first turn — doors locked, split into zones, every arc already widened. There is one dossier and one way out, across the roof. You have a stolen keycard, so getting around is cheap and being seen is the entire problem. This is not a robbery, it is twenty-four turns of not being caught — and one loud mistake puts the building into full lockdown with you still inside it.',
    rule: 'Already being searched. Take the dossier to the roof duct. Contact ends it.',
  },
};

export const MODE_IDS: readonly string[] = ['nightshift', 'brownout', 'hotfloor'];
export const DEFAULT_MODE = 'nightshift';

/**
 * Validate an id off a URL or off storage. `MODES[id] || DEFAULT` lets 'constructor' through as a Mode
 * of undefined fields, which reaches the generator as NaN and produces a building of no cells.
 */
export function modeOf(id: string | null | undefined): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE];
}
